import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type {
  Activity,
  ActivityActorKind,
  ActivityChannel,
  ActivityDirection,
  ActivityFileLink,
  ActivityOutcome,
  ActivityTarget,
  ActivityTargetKind,
} from "../../lib/types";

/**
 * Sprint 14 — Activity hooks. Activity is the interaction-history primitive.
 * Kuvio records nothing automatically; these write/read explicit rows via MCP.
 */

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["activities"] });
  qc.invalidateQueries({ queryKey: ["row-detail"] });
}

function unwrap<T>(result: { isError?: boolean; structuredContent?: T; content?: Array<{ text?: string }> }): T {
  if (result.isError) {
    throw new Error(result.content?.[0]?.text ?? "Request failed");
  }
  return result.structuredContent as T;
}

/** The primary "what has happened with X" query for any object. */
export function useActivitiesFor(
  target: { kind: ActivityTargetKind; type?: string; id: string } | undefined,
  limit = 20,
  includeArchived = false,
) {
  return useQuery<Activity[]>({
    queryKey: target ? [...queryKeys.activitiesFor(target.kind, target.id), includeArchived] : ["activities", "for", ""],
    queryFn: async () => {
      if (!target) return [];
      const r = await mcpClient.toolsCall<{ activities: Activity[] }>("list_activities_for", {
        target,
        limit,
        ...(includeArchived ? { include_archived: true } : {}),
      });
      return unwrap(r)?.activities ?? [];
    },
    enabled: !!target,
    staleTime: 15_000,
  });
}

/** Optional channel / since narrowing — Sprint 26.2 Activity filters. */
export interface ActivityFilterOpts {
  channel?: ActivityChannel;
  since?: string;
}

/** Activities where an agent (or other actor) is the originator — cockpit panel. */
export function useActivitiesByActor(
  actorKind: ActivityActorKind,
  actorId: string | undefined,
  limit = 20,
  includeArchived = false,
  filters: ActivityFilterOpts = {},
) {
  const { channel, since } = filters;
  return useQuery<Activity[]>({
    queryKey: actorId
      ? [...queryKeys.activitiesByActor(actorKind, actorId), includeArchived, channel ?? "", since ?? ""]
      : ["activities", "actor", ""],
    queryFn: async () => {
      if (!actorId) return [];
      const r = await mcpClient.toolsCall<{ activities: Activity[] }>("list_activities", {
        actor_kind: actorKind,
        actor_id: actorId,
        limit,
        ...(channel ? { channel } : {}),
        ...(since ? { since } : {}),
        ...(includeArchived ? { include_archived: true } : {}),
      });
      return unwrap(r)?.activities ?? [];
    },
    enabled: !!actorId,
    staleTime: 15_000,
  });
}

/**
 * Sprint 26.2 — the workspace-wide interaction stream with channel / actor /
 * since filters. Backs the Activity route. When `actorId` is set the actor is an
 * agent; omit it for "all actors".
 */
export function useWorkspaceActivities(opts: {
  channel?: ActivityChannel;
  actorId?: string;
  since?: string;
  limit?: number;
}) {
  const { channel, actorId, since, limit = 50 } = opts;
  return useQuery<Activity[]>({
    queryKey: ["activities", "workspace", channel ?? "", actorId ?? "", since ?? "", limit],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ activities: Activity[] }>("list_activities", {
        limit,
        ...(channel ? { channel } : {}),
        ...(actorId ? { actor_kind: "agent", actor_id: actorId } : {}),
        ...(since ? { since } : {}),
      });
      return unwrap(r)?.activities ?? [];
    },
    staleTime: 15_000,
  });
}

export interface CreateActivityParams {
  subject: string;
  channel: ActivityChannel;
  direction: ActivityDirection;
  occurred_at: string;
  actor: { kind: ActivityActorKind; id?: string; label?: string };
  regarding: ActivityTarget[];
  summary?: string;
  details?: Record<string, unknown>;
  outcome?: ActivityOutcome;
  external_id?: string;
  routine_ref?: string;
  file_refs?: ActivityFileLink[];
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateActivityParams): Promise<Activity> => {
      const r = await mcpClient.toolsCall<{ activity: Activity }>("create_activity", params as unknown as Record<string, unknown>);
      return unwrap(r).activity;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useArchiveActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("archive_activity", { id });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useRestoreActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("restore_activity", { id });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<void> => {
      const r = await mcpClient.toolsCall("delete_activity", { id, reason });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}
